/**
 * Long analyses (model analysis, Efficient Frontier, Monte Carlo) run as jobs
 * that outlive the component that started them.
 *
 *  - Leave the page (or the browser tab) while one runs, and JobNotifier shows
 *    a pop-up when it's ready; clicking it opens the result.
 *  - The result is kept here for 30 minutes, so reopening shows it at once
 *    instead of running the analysis again.
 *  - Asking again for a job that is still running joins it rather than
 *    starting a second copy.
 *
 * A component "watches" the job it is displaying (watchJob). A job that
 * finishes while watched, with the page visible, needs no pop-up: the user is
 * already looking at it.
 */

export interface JobMeta {
  label: string;      // "Efficient Frontier · Theme – Cybersecurity"
  route?: string;     // where the result is shown, e.g. /portfolio/models?id=73&tab=frontier
}

export interface JobNotice {
  key: string;
  label: string;
  route?: string;
  ok: boolean;
}

interface Job {
  meta: JobMeta;
  status: 'running' | 'done';
  startedAt: number;
  finishedAt?: number;
  promise: Promise<any>;
  result?: any;
}

const FRESH_MS = 30 * 60 * 1000;
const NOTIFY_AFTER_MS = 4000;      // quick answers don't need a pop-up

const jobs = new Map<string, Job>();
const watchers = new Map<string, number>();
const listeners = new Set<(n: JobNotice) => void>();

function announce(key: string, job: Job, ok: boolean) {
  const watched = (watchers.get(key) || 0) > 0 && !document.hidden;
  if (watched || Date.now() - job.startedAt < NOTIFY_AFTER_MS) return;
  const notice: JobNotice = { key, label: job.meta.label, route: job.meta.route, ok };
  listeners.forEach((fn) => fn(notice));
}

/** Run `fn` as a job under `key`, or return the running / recent result for it. */
export function runJob<T>(key: string, meta: JobMeta, fn: () => Promise<T>): Promise<T> {
  const existing = jobs.get(key);
  if (existing) {
    if (existing.status === 'running') {
      existing.meta = meta;
      return existing.promise;
    }
    if (Date.now() - (existing.finishedAt || 0) < FRESH_MS) return Promise.resolve(existing.result);
  }
  const job: Job = { meta, status: 'running', startedAt: Date.now(), promise: Promise.resolve() };
  job.promise = fn().then(
    (result) => {
      job.status = 'done';
      job.result = result;
      job.finishedAt = Date.now();
      // An analysis can come back with an error inside a 200 response.
      announce(key, job, !(result && typeof result === 'object' && (result as any).error));
      return result;
    },
    (err) => {
      jobs.delete(key);          // failures aren't cached; the next attempt runs again
      announce(key, job, false);
      throw err;
    },
  );
  jobs.set(key, job);
  return job.promise;
}

/** A running job, or a finished one still fresh, for `key` (to pick up where the user left off). */
export function hasJob(key: string): boolean {
  const j = jobs.get(key);
  return !!j && (j.status === 'running' || Date.now() - (j.finishedAt || 0) < FRESH_MS);
}

/** Mark `key` as being looked at while the calling component shows it. Returns the undo. */
export function watchJob(key: string | null | undefined): () => void {
  if (!key) return () => undefined;
  watchers.set(key, (watchers.get(key) || 0) + 1);
  return () => {
    const n = (watchers.get(key) || 1) - 1;
    if (n <= 0) watchers.delete(key); else watchers.set(key, n);
  };
}

export function onJobDone(fn: (n: JobNotice) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Stable key for arbitrary parameters. */
export function jobKey(kind: string, params: unknown): string {
  return `${kind}:${JSON.stringify(params)}`;
}
